import { normalizeLocale, type SupportedLocale } from '@/i18n/config';
import { createVietnameseStaticCopy } from '@/i18n/vietnamese-static-copy';
import { createIndonesianStaticCopy } from '@/i18n/indonesian-static-copy';
import { createTraditionalChineseStaticCopy } from '@/i18n/traditional-chinese-static-copy';

const { 'de-AT': _unusedDeAtStaticUiCopy, ...STATIC_UI_COPY_BASE } = {
  en: {
    close: 'Close',
    previous: 'Previous',
    next: 'Next',
    esc: 'ESC',
    fullscreenViewerDescription:
      'Fullscreen media viewer. Press Escape to close. Use left and right arrows to navigate.',
    loadingFullImage: 'Loading full image',
    loading: 'Loading...',
    loading3dViewer: 'Loading 3D viewer...',
    noSrcProvided: 'No source provided',
    failedToLoad3dViewerLibrary: 'Failed to load 3D viewer library',
    failedToLoad3dModel: 'Failed to load 3D model',
    failedToLoadVideo: 'Failed to load video',
    videoLoadingAborted: 'Video loading aborted',
    networkErrorLoadingVideo: 'Network error loading video',
    videoDecodingError: 'Video decoding error',
    videoFormatNotSupported: 'Video format not supported',
    noVideoSourceAvailable: 'No video source available',
    loadingVideo: 'Loading video...',
    videoAsset: 'Video Asset',
    urlNotAccessible: 'URL not accessible',
    urlAccessible: 'URL accessible',
    yes: 'Yes',
    no: 'No',
    source: 'Source',
    dialogClose: 'Close',
    sidebarTitle: 'Sidebar',
    sidebarDescription: 'Displays the mobile sidebar.',
    toggleSidebar: 'Toggle Sidebar',
    paginationPrevious: 'Previous',
    paginationNext: 'Next',
    paginationMorePages: 'More pages',
    paginationGoToPreviousPage: 'Go to previous page',
    paginationGoToNextPage: 'Go to next page',
    pageNotFound: 'Page not found',
    pageNotFoundDescription:
      "The page you are looking for doesn't exist or has been moved.",
    accessDenied: 'Access denied',
    accessDeniedDescription:
      "You don't have permission to view this page. Ask a workspace admin for access if you think this is a mistake.",
    goBack: 'Go back',
    goHome: 'Go home',
    goToDashboard: 'Go to dashboard',
    errorLabel: 'Error',
    somethingWentWrong: 'Something went wrong',
    unexpectedError: 'An unexpected error occurred.',
    tryAgain: 'Try again',
    reconnecting: 'Reconnecting…',
    pleaseTryAgainContactSupport:
      'Please try again. If this keeps happening, contact support.',
    reference: 'Reference',
    suggestions: 'Suggestions',
  },
  'pt-BR': {
    close: 'Feche.',
    previous: 'Anterior',
    next: 'Pr\u00F3xima',
    esc: 'ESC',
    fullscreenViewerDescription:
      'Visualizador de m\u00EDdia em tela cheia. Pressione Escape para fechar. Use setas esquerda e direita para navegar.',
    loadingFullImage: 'Carregando imagem completa',
    loading: 'Carregando...',
    loading3dViewer: 'Carregando visualizador 3D...',
    noSrcProvided: 'Nenhuma fonte fornecida.',
    failedToLoad3dViewerLibrary: 'Falha ao carregar a biblioteca 3D',
    failedToLoad3dModel: 'Falha ao carregar o modelo 3D.',
    failedToLoadVideo: 'N\u00E3o conseguiu carregar o v\u00EDdeo.',
    videoLoadingAborted: 'Carregamento de v\u00EDdeo abortado',
    networkErrorLoadingVideo: 'Erro de rede carregando v\u00EDdeo',
    videoDecodingError: 'Erro de decodifica\u00E7\u00E3o de v\u00EDdeo',
    videoFormatNotSupported: 'Formato de v\u00EDdeo n\u00E3o suportado',
    noVideoSourceAvailable: 'Nenhuma fonte de v\u00EDdeo dispon\u00EDvel.',
    loadingVideo: 'Carregando v\u00EDdeo...',
    videoAsset: 'Activo de V\u00EDdeo',
    urlNotAccessible: 'URL n\u00E3o acess\u00EDvel',
    urlAccessible: 'URL acess\u00EDvel',
    yes: 'Sim.',
    no: 'N\u00E3o.',
    source: 'Fonte',
    dialogClose: 'Feche.',
    sidebarTitle: 'Barra lateral',
    sidebarDescription: 'Mostra a barra lateral m\u00F3vel.',
    toggleSidebar: 'Alternar barra lateral',
    paginationPrevious: 'Anterior',
    paginationNext: 'Pr\u00F3xima',
    paginationMorePages: 'Mais p\u00E1ginas',
    paginationGoToPreviousPage: 'V\u00E1 para a p\u00E1gina anterior.',
    paginationGoToNextPage: 'V\u00E1 para a pr\u00F3xima p\u00E1gina.',
    pageNotFound: 'P\u00E1gina n\u00E3o encontrada',
    pageNotFoundDescription:
      'A p\u00E1gina que voc\u00EA est\u00E1 procurando n\u00E3o existe ou foi movida.',
    accessDenied: 'Acesso negado.',
    accessDeniedDescription:
      'Voc\u00EA n\u00E3o tem permiss\u00E3o para ver esta p\u00E1gina. Pe\u00E7a acesso a um administrador do espa\u00E7o de trabalho se acha que isso \u00E9 um erro.',
    goBack: 'Volte.',
    goHome: 'V\u00E1 para casa.',
    goToDashboard: 'V\u00E1 para o painel.',
    errorLabel: 'Erro',
    somethingWentWrong: 'Algo deu errado.',
    unexpectedError: 'Um erro inesperado ocorreu.',
    tryAgain: 'Tente novamente.',
    reconnecting: 'Reconectando…',
    pleaseTryAgainContactSupport:
      'Por favor, tente de novo. Se isso continuar acontecendo, contate o suporte.',
    reference: 'Refer\u00EAncia',
    suggestions: 'Sugest\u00F5es',
  },
  ar: {
    close: 'إغلاق',
    previous: 'السابق',
    next: 'التالي',
    esc: 'خروج',
    fullscreenViewerDescription:
      'عارض وسائط بملء الشاشة. اضغط على Escape للإغلاق. استخدم السهمين الأيسر والأيمن للتنقل.',
    loadingFullImage: 'جارٍ تحميل الصورة الكاملة',
    loading: 'جارٍ التحميل...',
    loading3dViewer: 'جارٍ تحميل عارض ثلاثي الأبعاد...',
    noSrcProvided: 'لم يتم توفير مصدر',
    failedToLoad3dViewerLibrary: 'تعذر تحميل مكتبة العارض ثلاثي الأبعاد',
    failedToLoad3dModel: 'تعذر تحميل النموذج ثلاثي الأبعاد',
    failedToLoadVideo: 'تعذر تحميل الفيديو',
    videoLoadingAborted: 'تم إيقاف تحميل الفيديو',
    networkErrorLoadingVideo: 'خطأ في الشبكة أثناء تحميل الفيديو',
    videoDecodingError: 'خطأ في فك ترميز الفيديو',
    videoFormatNotSupported: 'تنسيق الفيديو غير مدعوم',
    noVideoSourceAvailable: 'لا يوجد مصدر فيديو متاح',
    loadingVideo: 'جارٍ تحميل الفيديو...',
    videoAsset: 'أصل الفيديو',
    urlNotAccessible: 'تعذر الوصول إلى الرابط',
    urlAccessible: 'إمكانية الوصول إلى الرابط',
    yes: 'نعم',
    no: 'لا',
    source: 'المصدر',
    dialogClose: 'إغلاق',
    sidebarTitle: 'الشريط الجانبي',
    sidebarDescription: 'يعرض الشريط الجانبي على الهاتف.',
    toggleSidebar: 'تبديل الشريط الجانبي',
    paginationPrevious: 'السابق',
    paginationNext: 'التالي',
    paginationMorePages: 'صفحات إضافية',
    paginationGoToPreviousPage: 'الانتقال إلى الصفحة السابقة',
    paginationGoToNextPage: 'الانتقال إلى الصفحة التالية',
    pageNotFound: 'الصفحة غير موجودة',
    pageNotFoundDescription: 'الصفحة التي تبحث عنها غير موجودة أو تم نقلها.',
    accessDenied: 'تم رفض الوصول',
    accessDeniedDescription:
      'ليس لديك إذن لعرض هذه الصفحة. اطلب الوصول من مسؤول مساحة العمل إذا كنت تعتقد أن هذا خطأ.',
    goBack: 'رجوع',
    goHome: 'الذهاب إلى الصفحة الرئيسية',
    goToDashboard: 'الذهاب إلى لوحة التحكم',
    errorLabel: 'خطأ',
    somethingWentWrong: 'حدث خطأ ما',
    unexpectedError: 'حدث خطأ غير متوقع.',
    tryAgain: 'حاول مرة أخرى',
    reconnecting: 'جارٍ إعادة الاتصال…',
    pleaseTryAgainContactSupport:
      'يرجى المحاولة مرة أخرى. إذا استمرت المشكلة، تواصل مع الدعم.',
    reference: 'المرجع',
    suggestions: 'اقتراحات',
  },
  de: {
    close: 'Schließen',
    previous: 'Zurück',
    next: 'Weiter',
    esc: 'ESC',
    fullscreenViewerDescription:
      'Vollbild-Medienansicht. Drücke Escape zum Schließen. Verwende die Pfeiltasten links und rechts zum Navigieren.',
    loadingFullImage: 'Volles Bild wird geladen',
    loading: 'Wird geladen...',
    loading3dViewer: '3D-Viewer wird geladen...',
    noSrcProvided: 'Keine Quelle angegeben',
    failedToLoad3dViewerLibrary:
      '3D-Viewer-Bibliothek konnte nicht geladen werden',
    failedToLoad3dModel: '3D-Modell konnte nicht geladen werden',
    failedToLoadVideo: 'Video konnte nicht geladen werden',
    videoLoadingAborted: 'Videoladen abgebrochen',
    networkErrorLoadingVideo: 'Netzwerkfehler beim Laden des Videos',
    videoDecodingError: 'Fehler beim Dekodieren des Videos',
    videoFormatNotSupported: 'Videoformat wird nicht unterstützt',
    noVideoSourceAvailable: 'Keine Videoquelle verfügbar',
    loadingVideo: 'Video wird geladen...',
    videoAsset: 'Video-Asset',
    urlNotAccessible: 'URL nicht erreichbar',
    urlAccessible: 'URL erreichbar',
    yes: 'Ja',
    no: 'Nein',
    source: 'Quelle',
    dialogClose: 'Schließen',
    sidebarTitle: 'Seitenleiste',
    sidebarDescription: 'Zeigt die mobile Seitenleiste an.',
    toggleSidebar: 'Seitenleiste umschalten',
    paginationPrevious: 'Zurück',
    paginationNext: 'Weiter',
    paginationMorePages: 'Mehr Seiten',
    paginationGoToPreviousPage: 'Zur vorherigen Seite',
    paginationGoToNextPage: 'Zur nächsten Seite',
    pageNotFound: 'Seite nicht gefunden',
    pageNotFoundDescription:
      'Die gesuchte Seite existiert nicht oder wurde verschoben.',
    accessDenied: 'Zugriff verweigert',
    accessDeniedDescription:
      'Du hast keine Berechtigung, diese Seite anzusehen. Bitte einen Workspace-Admin um Zugriff, falls du dies für einen Fehler hältst.',
    goBack: 'Zurück',
    goHome: 'Zur Startseite',
    goToDashboard: 'Zum Dashboard',
    errorLabel: 'Fehler',
    somethingWentWrong: 'Etwas ist schiefgelaufen',
    unexpectedError: 'Ein unerwarteter Fehler ist aufgetreten.',
    tryAgain: 'Erneut versuchen',
    reconnecting: 'Verbindung wird wiederhergestellt…',
    pleaseTryAgainContactSupport:
      'Bitte versuche es erneut. Wenn das weiterhin passiert, kontaktiere den Support.',
    reference: 'Referenz',
    suggestions: 'Vorschläge',
  },
  'de-AT': {
    close: 'Schließen',
    previous: 'Zurück',
    next: 'Weiter',
    esc: 'ESC',
    fullscreenViewerDescription:
      'Vollbild-Medienansicht. Drücke Escape zum Schließen. Verwende die Pfeiltasten links und rechts zum Navigieren.',
    loadingFullImage: 'Volles Bild wird geladen',
    loading: 'Wird geladen...',
    loading3dViewer: '3D-Viewer wird geladen...',
    noSrcProvided: 'Keine Quelle angegeben',
    failedToLoad3dViewerLibrary:
      '3D-Viewer-Bibliothek konnte nicht geladen werden',
    failedToLoad3dModel: '3D-Modell konnte nicht geladen werden',
    failedToLoadVideo: 'Video konnte nicht geladen werden',
    videoLoadingAborted: 'Videoladen abgebrochen',
    networkErrorLoadingVideo: 'Netzwerkfehler beim Laden des Videos',
    videoDecodingError: 'Fehler beim Dekodieren des Videos',
    videoFormatNotSupported: 'Videoformat wird nicht unterstützt',
    noVideoSourceAvailable: 'Keine Videoquelle verfügbar',
    loadingVideo: 'Video wird geladen...',
    videoAsset: 'Video-Asset',
    urlNotAccessible: 'URL nicht erreichbar',
    urlAccessible: 'URL erreichbar',
    yes: 'Ja',
    no: 'Nein',
    source: 'Quelle',
    dialogClose: 'Schließen',
    sidebarTitle: 'Seitenleiste',
    sidebarDescription: 'Zeigt die mobile Seitenleiste an.',
    toggleSidebar: 'Seitenleiste umschalten',
    paginationPrevious: 'Zurück',
    paginationNext: 'Weiter',
    paginationMorePages: 'Mehr Seiten',
    paginationGoToPreviousPage: 'Zur vorherigen Seite',
    paginationGoToNextPage: 'Zur nächsten Seite',
    pageNotFound: 'Seite nicht gefunden',
    pageNotFoundDescription:
      'Die gesuchte Seite existiert nicht oder wurde verschoben.',
    accessDenied: 'Zugriff verweigert',
    accessDeniedDescription:
      'Du hast keine Berechtigung, diese Seite anzusehen. Bitte einen Workspace-Admin um Zugriff, falls du dies für einen Fehler hältst.',
    goBack: 'Zurück',
    goHome: 'Zur Startseite',
    goToDashboard: 'Zum Dashboard',
    errorLabel: 'Fehler',
    somethingWentWrong: 'Etwas ist schiefgelaufen',
    unexpectedError: 'Ein unerwarteter Fehler ist aufgetreten.',
    tryAgain: 'Erneut versuchen',
    reconnecting: 'Verbindung wird wiederhergestellt…',
    pleaseTryAgainContactSupport:
      'Bitte versuche es erneut. Wenn das weiterhin passiert, kontaktiere den Support.',
    reference: 'Referenz',
    suggestions: 'Vorschläge',
  },
  es: {
    close: 'Cerrar',
    previous: 'Anterior',
    next: 'Siguiente',
    esc: 'ESC',
    fullscreenViewerDescription:
      'Visor multimedia en pantalla completa. Pulsa Escape para cerrar. Usa las flechas izquierda y derecha para navegar.',
    loadingFullImage: 'Cargando imagen completa',
    loading: 'Cargando...',
    loading3dViewer: 'Cargando visor 3D...',
    noSrcProvided: 'No se proporcionó origen',
    failedToLoad3dViewerLibrary: 'No se pudo cargar la biblioteca del visor 3D',
    failedToLoad3dModel: 'No se pudo cargar el modelo 3D',
    failedToLoadVideo: 'No se pudo cargar el video',
    videoLoadingAborted: 'Se canceló la carga del video',
    networkErrorLoadingVideo: 'Error de red al cargar el video',
    videoDecodingError: 'Error al decodificar el video',
    videoFormatNotSupported: 'Formato de video no compatible',
    noVideoSourceAvailable: 'No hay una fuente de video disponible',
    loadingVideo: 'Cargando video...',
    videoAsset: 'Recurso de video',
    urlNotAccessible: 'URL no accesible',
    urlAccessible: 'URL accesible',
    yes: 'Sí',
    no: 'No',
    source: 'Fuente',
    dialogClose: 'Cerrar',
    sidebarTitle: 'Barra lateral',
    sidebarDescription: 'Muestra la barra lateral móvil.',
    toggleSidebar: 'Alternar barra lateral',
    paginationPrevious: 'Anterior',
    paginationNext: 'Siguiente',
    paginationMorePages: 'Más páginas',
    paginationGoToPreviousPage: 'Ir a la página anterior',
    paginationGoToNextPage: 'Ir a la página siguiente',
    pageNotFound: 'Página no encontrada',
    pageNotFoundDescription: 'La página que buscas no existe o fue movida.',
    accessDenied: 'Acceso denegado',
    accessDeniedDescription:
      'No tienes permiso para ver esta página. Solicita acceso a un administrador del espacio de trabajo si crees que es un error.',
    goBack: 'Volver',
    goHome: 'Ir al inicio',
    goToDashboard: 'Ir al panel',
    errorLabel: 'Error',
    somethingWentWrong: 'Algo salió mal',
    unexpectedError: 'Ocurrió un error inesperado.',
    tryAgain: 'Intentar de nuevo',
    reconnecting: 'Reconectando…',
    pleaseTryAgainContactSupport:
      'Inténtalo de nuevo. Si esto sigue ocurriendo, contacta con soporte.',
    reference: 'Referencia',
    suggestions: 'Sugerencias',
  },
  fr: {
    close: 'Fermer',
    previous: 'Précédent',
    next: 'Suivant',
    esc: 'ESC',
    fullscreenViewerDescription:
      'Visionneuse multimédia en plein écran. Appuyez sur Échap pour fermer. Utilisez les flèches gauche et droite pour naviguer.',
    loadingFullImage: 'Chargement de l’image complète',
    loading: 'Chargement...',
    loading3dViewer: 'Chargement du visualiseur 3D...',
    noSrcProvided: 'Aucune source fournie',
    failedToLoad3dViewerLibrary:
      'Impossible de charger la bibliothèque du visualiseur 3D',
    failedToLoad3dModel: 'Impossible de charger le modèle 3D',
    failedToLoadVideo: 'Impossible de charger la vidéo',
    videoLoadingAborted: 'Chargement de la vidéo annulé',
    networkErrorLoadingVideo: 'Erreur réseau lors du chargement de la vidéo',
    videoDecodingError: 'Erreur de décodage de la vidéo',
    videoFormatNotSupported: 'Format vidéo non pris en charge',
    noVideoSourceAvailable: 'Aucune source vidéo disponible',
    loadingVideo: 'Chargement de la vidéo...',
    videoAsset: 'Ressource vidéo',
    urlNotAccessible: 'URL inaccessible',
    urlAccessible: 'URL accessible',
    yes: 'Oui',
    no: 'Non',
    source: 'Source',
    dialogClose: 'Fermer',
    sidebarTitle: 'Barre latérale',
    sidebarDescription: 'Affiche la barre latérale mobile.',
    toggleSidebar: 'Basculer la barre latérale',
    paginationPrevious: 'Précédent',
    paginationNext: 'Suivant',
    paginationMorePages: 'Plus de pages',
    paginationGoToPreviousPage: 'Aller à la page précédente',
    paginationGoToNextPage: 'Aller à la page suivante',
    pageNotFound: 'Page introuvable',
    pageNotFoundDescription:
      'La page que vous recherchez n’existe pas ou a été déplacée.',
    accessDenied: 'Accès refusé',
    accessDeniedDescription:
      'Vous n’avez pas l’autorisation de voir cette page. Demandez l’accès à un administrateur de l’espace de travail si vous pensez qu’il s’agit d’une erreur.',
    goBack: 'Retour',
    goHome: 'Aller à l’accueil',
    goToDashboard: 'Aller au tableau de bord',
    errorLabel: 'Erreur',
    somethingWentWrong: 'Un problème est survenu',
    unexpectedError: 'Une erreur inattendue est survenue.',
    tryAgain: 'Réessayer',
    reconnecting: 'Reconnexion…',
    pleaseTryAgainContactSupport:
      'Veuillez réessayer. Si le problème persiste, contactez le support.',
    reference: 'Référence',
    suggestions: 'Suggestions',
  },
  hi: {
    close: 'बंद करें',
    previous: 'पिछला',
    next: 'अगला',
    esc: 'ESC',
    fullscreenViewerDescription:
      'फुलस्क्रीन मीडिया व्यूअर। बंद करने के लिए Escape दबाएं। नेविगेट करने के लिए बाएँ और दाएँ तीर कुंजियों का उपयोग करें।',
    loadingFullImage: 'पूरी इमेज लोड हो रही है',
    loading: 'लोड हो रहा है...',
    loading3dViewer: '3D व्यूअर लोड हो रहा है...',
    noSrcProvided: 'कोई स्रोत प्रदान नहीं किया गया',
    failedToLoad3dViewerLibrary: '3D व्यूअर लाइब्रेरी लोड नहीं हो सकी',
    failedToLoad3dModel: '3D मॉडल लोड नहीं हो सका',
    failedToLoadVideo: 'वीडियो लोड नहीं हो सका',
    videoLoadingAborted: 'वीडियो लोडिंग रद्द हुई',
    networkErrorLoadingVideo: 'वीडियो लोड करते समय नेटवर्क त्रुटि',
    videoDecodingError: 'वीडियो डिकोडिंग त्रुटि',
    videoFormatNotSupported: 'वीडियो फ़ॉर्मैट समर्थित नहीं है',
    noVideoSourceAvailable: 'कोई वीडियो स्रोत उपलब्ध नहीं है',
    loadingVideo: 'वीडियो लोड हो रहा है...',
    videoAsset: 'वीडियो एसेट',
    urlNotAccessible: 'URL उपलब्ध नहीं है',
    urlAccessible: 'URL उपलब्ध है',
    yes: 'हाँ',
    no: 'नहीं',
    source: 'स्रोत',
    dialogClose: 'बंद करें',
    sidebarTitle: 'साइडबार',
    sidebarDescription: 'मोबाइल साइडबार दिखाता है।',
    toggleSidebar: 'साइडबार टॉगल करें',
    paginationPrevious: 'पिछला',
    paginationNext: 'अगला',
    paginationMorePages: 'और पेज',
    paginationGoToPreviousPage: 'पिछले पेज पर जाएँ',
    paginationGoToNextPage: 'अगले पेज पर जाएँ',
    pageNotFound: 'पेज नहीं मिला',
    pageNotFoundDescription:
      'जिस पेज को आप खोज रहे हैं वह मौजूद नहीं है या उसे स्थानांतरित कर दिया गया है।',
    accessDenied: 'पहुंच अस्वीकृत',
    accessDeniedDescription:
      'आपको यह पेज देखने की अनुमति नहीं है। अगर आपको लगता है कि यह गलती है, तो वर्कस्पेस एडमिन से एक्सेस मांगें।',
    goBack: 'वापस जाएं',
    goHome: 'होम पर जाएँ',
    goToDashboard: 'डैशबोर्ड पर जाएँ',
    errorLabel: 'त्रुटि',
    somethingWentWrong: 'कुछ गलत हो गया',
    unexpectedError: 'एक अप्रत्याशित त्रुटि हुई।',
    tryAgain: 'फिर से कोशिश करें',
    reconnecting: 'फिर से कनेक्ट हो रहा है…',
    pleaseTryAgainContactSupport:
      'कृपया फिर से कोशिश करें। यदि यह बार-बार हो, तो सहायता से संपर्क करें।',
    reference: 'संदर्भ',
    suggestions: 'सुझाव',
  },
  it: {
    close: 'Chiudi',
    previous: 'Precedente',
    next: 'Successivo',
    esc: 'ESC',
    fullscreenViewerDescription:
      'Visualizzatore multimediale a schermo intero. Premi Esc per chiudere. Usa le frecce sinistra e destra per navigare.',
    loadingFullImage: 'Caricamento immagine completa',
    loading: 'Caricamento...',
    loading3dViewer: 'Caricamento visualizzatore 3D...',
    noSrcProvided: 'Nessuna sorgente fornita',
    failedToLoad3dViewerLibrary:
      'Impossibile caricare la libreria del visualizzatore 3D',
    failedToLoad3dModel: 'Impossibile caricare il modello 3D',
    failedToLoadVideo: 'Impossibile caricare il video',
    videoLoadingAborted: 'Caricamento video interrotto',
    networkErrorLoadingVideo: 'Errore di rete durante il caricamento del video',
    videoDecodingError: 'Errore di decodifica del video',
    videoFormatNotSupported: 'Formato video non supportato',
    noVideoSourceAvailable: 'Nessuna sorgente video disponibile',
    loadingVideo: 'Caricamento video...',
    videoAsset: 'Risorsa video',
    urlNotAccessible: 'URL non accessibile',
    urlAccessible: 'URL accessibile',
    yes: 'Sì',
    no: 'No',
    source: 'Sorgente',
    dialogClose: 'Chiudi',
    sidebarTitle: 'Barra laterale',
    sidebarDescription: 'Mostra la barra laterale mobile.',
    toggleSidebar: 'Attiva/disattiva barra laterale',
    paginationPrevious: 'Precedente',
    paginationNext: 'Successivo',
    paginationMorePages: 'Altre pagine',
    paginationGoToPreviousPage: 'Vai alla pagina precedente',
    paginationGoToNextPage: 'Vai alla pagina successiva',
    pageNotFound: 'Pagina non trovata',
    pageNotFoundDescription:
      'La pagina che stai cercando non esiste o è stata spostata.',
    accessDenied: 'Accesso negato',
    accessDeniedDescription:
      'Non hai l’autorizzazione per visualizzare questa pagina. Chiedi l’accesso a un amministratore dello spazio di lavoro se ritieni che sia un errore.',
    goBack: 'Indietro',
    goHome: 'Vai alla home',
    goToDashboard: 'Vai alla dashboard',
    errorLabel: 'Errore',
    somethingWentWrong: 'Qualcosa è andato storto',
    unexpectedError: 'Si è verificato un errore imprevisto.',
    tryAgain: 'Riprova',
    reconnecting: 'Riconnessione…',
    pleaseTryAgainContactSupport:
      'Riprova. Se continua a succedere, contatta l’assistenza.',
    reference: 'Riferimento',
    suggestions: 'Suggerimenti',
  },
  ja: {
    close: '閉じる',
    previous: '前へ',
    next: '次へ',
    esc: 'ESC',
    fullscreenViewerDescription:
      '全画面メディアビューアーです。閉じるには Escape を押してください。左右の矢印キーで移動できます。',
    loadingFullImage: '高解像度画像を読み込み中',
    loading: '読み込み中...',
    loading3dViewer: '3Dビューアーを読み込み中...',
    noSrcProvided: 'ソースが指定されていません',
    failedToLoad3dViewerLibrary:
      '3Dビューアーのライブラリを読み込めませんでした',
    failedToLoad3dModel: '3Dモデルを読み込めませんでした',
    failedToLoadVideo: '動画を読み込めませんでした',
    videoLoadingAborted: '動画の読み込みが中断されました',
    networkErrorLoadingVideo:
      '動画の読み込み中にネットワークエラーが発生しました',
    videoDecodingError: '動画のデコード中にエラーが発生しました',
    videoFormatNotSupported: 'この動画形式はサポートされていません',
    noVideoSourceAvailable: '利用可能な動画ソースがありません',
    loadingVideo: '動画を読み込み中...',
    videoAsset: '動画アセット',
    urlNotAccessible: 'URLにアクセスできません',
    urlAccessible: 'URLにアクセス可能',
    yes: 'はい',
    no: 'いいえ',
    source: 'ソース',
    dialogClose: '閉じる',
    sidebarTitle: 'サイドバー',
    sidebarDescription: 'モバイル用サイドバーを表示します。',
    toggleSidebar: 'サイドバーを切り替え',
    paginationPrevious: '前へ',
    paginationNext: '次へ',
    paginationMorePages: 'さらにページがあります',
    paginationGoToPreviousPage: '前のページへ移動',
    paginationGoToNextPage: '次のページへ移動',
    pageNotFound: 'ページが見つかりません',
    pageNotFoundDescription:
      'お探しのページは存在しないか、移動された可能性があります。',
    accessDenied: 'アクセスが拒否されました',
    accessDeniedDescription:
      'このページを表示する権限がありません。誤りだと思われる場合は、ワークスペース管理者にアクセスを依頼してください。',
    goBack: '戻る',
    goHome: 'ホームへ戻る',
    goToDashboard: 'ダッシュボードへ移動',
    errorLabel: 'エラー',
    somethingWentWrong: '問題が発生しました',
    unexpectedError: '予期しないエラーが発生しました。',
    tryAgain: '再試行',
    reconnecting: '再接続しています…',
    pleaseTryAgainContactSupport:
      'もう一度お試しください。問題が続く場合はサポートへお問い合わせください。',
    reference: '参照',
    suggestions: '候補',
  },
  ko: {
    close: '닫기',
    previous: '이전',
    next: '다음',
    esc: 'ESC',
    fullscreenViewerDescription:
      '전체 화면 미디어 뷰어입니다. 닫으려면 Escape를 누르세요. 좌우 화살표 키로 이동할 수 있습니다.',
    loadingFullImage: '전체 이미지를 불러오는 중',
    loading: '불러오는 중...',
    loading3dViewer: '3D 뷰어를 불러오는 중...',
    noSrcProvided: '소스가 제공되지 않았습니다',
    failedToLoad3dViewerLibrary: '3D 뷰어 라이브러리를 불러오지 못했습니다',
    failedToLoad3dModel: '3D 모델을 불러오지 못했습니다',
    failedToLoadVideo: '비디오를 불러오지 못했습니다',
    videoLoadingAborted: '비디오 로딩이 중단되었습니다',
    networkErrorLoadingVideo: '비디오 로딩 중 네트워크 오류가 발생했습니다',
    videoDecodingError: '비디오 디코딩 오류',
    videoFormatNotSupported: '지원되지 않는 비디오 형식입니다',
    noVideoSourceAvailable: '사용 가능한 비디오 소스가 없습니다',
    loadingVideo: '비디오를 불러오는 중...',
    videoAsset: '비디오 자산',
    urlNotAccessible: 'URL에 접근할 수 없습니다',
    urlAccessible: 'URL 접근 가능',
    yes: '예',
    no: '아니요',
    source: '소스',
    dialogClose: '닫기',
    sidebarTitle: '사이드바',
    sidebarDescription: '모바일 사이드바를 표시합니다.',
    toggleSidebar: '사이드바 전환',
    paginationPrevious: '이전',
    paginationNext: '다음',
    paginationMorePages: '더 많은 페이지',
    paginationGoToPreviousPage: '이전 페이지로 이동',
    paginationGoToNextPage: '다음 페이지로 이동',
    pageNotFound: '페이지를 찾을 수 없습니다',
    pageNotFoundDescription: '찾고 있는 페이지가 없거나 이동되었습니다.',
    accessDenied: '접근이 거부되었습니다',
    accessDeniedDescription:
      '이 페이지를 볼 권한이 없습니다. 잘못된 것이라면 워크스페이스 관리자에게 접근 권한을 요청하세요.',
    goBack: '뒤로 가기',
    goHome: '홈으로 이동',
    goToDashboard: '대시보드로 이동',
    errorLabel: '오류',
    somethingWentWrong: '문제가 발생했습니다',
    unexpectedError: '예상치 못한 오류가 발생했습니다.',
    tryAgain: '다시 시도',
    reconnecting: '다시 연결하는 중…',
    pleaseTryAgainContactSupport:
      '다시 시도해 주세요. 계속되면 지원팀에 문의해 주세요.',
    reference: '참조',
    suggestions: '제안',
  },
  ru: {
    close: 'Закрыть',
    previous: 'Назад',
    next: 'Далее',
    esc: 'ESC',
    fullscreenViewerDescription:
      'Полноэкранный просмотр медиа. Нажмите Escape, чтобы закрыть. Используйте стрелки влево и вправо для навигации.',
    loadingFullImage: 'Загрузка полного изображения',
    loading: 'Загрузка...',
    loading3dViewer: 'Загрузка 3D-просмотрщика...',
    noSrcProvided: 'Источник не указан',
    failedToLoad3dViewerLibrary:
      'Не удалось загрузить библиотеку 3D-просмотрщика',
    failedToLoad3dModel: 'Не удалось загрузить 3D-модель',
    failedToLoadVideo: 'Не удалось загрузить видео',
    videoLoadingAborted: 'Загрузка видео прервана',
    networkErrorLoadingVideo: 'Сетевая ошибка при загрузке видео',
    videoDecodingError: 'Ошибка декодирования видео',
    videoFormatNotSupported: 'Формат видео не поддерживается',
    noVideoSourceAvailable: 'Источник видео недоступен',
    loadingVideo: 'Загрузка видео...',
    videoAsset: 'Видеоассет',
    urlNotAccessible: 'URL недоступен',
    urlAccessible: 'URL доступен',
    yes: 'Да',
    no: 'Нет',
    source: 'Источник',
    dialogClose: 'Закрыть',
    sidebarTitle: 'Боковая панель',
    sidebarDescription: 'Отображает мобильную боковую панель.',
    toggleSidebar: 'Переключить боковую панель',
    paginationPrevious: 'Назад',
    paginationNext: 'Далее',
    paginationMorePages: 'Больше страниц',
    paginationGoToPreviousPage: 'Перейти на предыдущую страницу',
    paginationGoToNextPage: 'Перейти на следующую страницу',
    pageNotFound: 'Страница не найдена',
    pageNotFoundDescription:
      'Страница, которую вы ищете, не существует или была перемещена.',
    accessDenied: 'Доступ запрещён',
    accessDeniedDescription:
      'У вас нет разрешения на просмотр этой страницы. Если вы считаете, что это ошибка, запросите доступ у администратора рабочего пространства.',
    goBack: 'Назад',
    goHome: 'На главную',
    goToDashboard: 'Перейти в панель',
    errorLabel: 'Ошибка',
    somethingWentWrong: 'Что-то пошло не так',
    unexpectedError: 'Произошла непредвиденная ошибка.',
    tryAgain: 'Попробовать снова',
    reconnecting: 'Переподключение…',
    pleaseTryAgainContactSupport:
      'Пожалуйста, попробуйте снова. Если это продолжится, обратитесь в поддержку.',
    reference: 'Ссылка',
    suggestions: 'Предложения',
  },
  zh: {
    close: '关闭',
    previous: '上一个',
    next: '下一个',
    esc: '退出',
    fullscreenViewerDescription:
      '全屏媒体查看器。按 Escape 可关闭，使用左右方向键进行切换。',
    loadingFullImage: '正在加载完整图片',
    loading: '加载中...',
    loading3dViewer: '正在加载 3D 查看器...',
    noSrcProvided: '未提供资源地址',
    failedToLoad3dViewerLibrary: '无法加载 3D 查看器库',
    failedToLoad3dModel: '无法加载 3D 模型',
    failedToLoadVideo: '无法加载视频',
    videoLoadingAborted: '视频加载已中止',
    networkErrorLoadingVideo: '加载视频时发生网络错误',
    videoDecodingError: '视频解码错误',
    videoFormatNotSupported: '不支持的视频格式',
    noVideoSourceAvailable: '没有可用的视频源',
    loadingVideo: '正在加载视频...',
    videoAsset: '视频素材',
    urlNotAccessible: 'URL 无法访问',
    urlAccessible: 'URL 可访问',
    yes: '是',
    no: '否',
    source: '来源',
    dialogClose: '关闭',
    sidebarTitle: '侧边栏',
    sidebarDescription: '显示移动端侧边栏。',
    toggleSidebar: '切换侧边栏',
    paginationPrevious: '上一页',
    paginationNext: '下一页',
    paginationMorePages: '更多页面',
    paginationGoToPreviousPage: '转到上一页',
    paginationGoToNextPage: '转到下一页',
    pageNotFound: '页面未找到',
    pageNotFoundDescription: '你要查找的页面不存在，或已被移动。',
    accessDenied: '无权访问',
    accessDeniedDescription:
      '你没有权限查看此页面。如果你认为这是错误，请向工作区管理员申请访问权限。',
    goBack: '返回',
    goHome: '返回首页',
    goToDashboard: '前往仪表盘',
    errorLabel: '错误',
    somethingWentWrong: '出现了一些问题',
    unexpectedError: '发生了意外错误。',
    tryAgain: '重试',
    reconnecting: '正在重新连接…',
    pleaseTryAgainContactSupport: '请重试。如果问题持续出现，请联系支持团队。',
    reference: '参考',
    suggestions: '建议',
  },
} as const;

export const STATIC_UI_COPY = {
  ...STATIC_UI_COPY_BASE,
  'zh-TW': createTraditionalChineseStaticCopy(STATIC_UI_COPY_BASE.zh),
  vi: createVietnameseStaticCopy(STATIC_UI_COPY_BASE.en),
  id: createIndonesianStaticCopy(STATIC_UI_COPY_BASE.en),
} as const satisfies Record<SupportedLocale, Record<string, string>>;

export function getStaticUiCopy(locale: string) {
  const normalizedLocale: SupportedLocale = normalizeLocale(locale);

  return STATIC_UI_COPY[normalizedLocale];
}
